import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackRemoveFromCart } from '../utils/analytics';

const CartContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [] });
  const [cartId, setCartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const storedCartId = localStorage.getItem('cartId');
    if (storedCartId) {
      setCartId(storedCartId);
      fetchCart(storedCartId);
    } else {
      createCart();
    }
  }, []);

  const createCart = async () => {
    try {
      const response = await axios.post(`${API}/cart`);
      const newCartId = response.data.cart_id;
      setCartId(newCartId);
      localStorage.setItem('cartId', newCartId);
    } catch (error) {
      console.error('Error creating cart:', error);
    }
  };

  const fetchCart = async (id) => {
    try {
      const response = await axios.get(`${API}/cart/${id}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      createCart();
    }
  };

  const addToCart = useCallback(async (product) => {
    let activeCartId = cartId;
    if (!activeCartId) {
      try {
        const response = await axios.post(`${API}/cart`);
        activeCartId = response.data.cart_id;
        setCartId(activeCartId);
        localStorage.setItem('cartId', activeCartId);
      } catch (error) {
        console.error('Error creating cart:', error);
        return false;
      }
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/cart/${activeCartId}/items`, {
        product_id: product.product_id || product.id,
        name: product.name,
        price: parseFloat(product.price),
        quantity: 1,
        image: product.images?.[0]?.src || product.image || null,
        country: product.country_code || null,
        parent_id: product.parent_id || null,
        package_type_id: product.package_type_id || null,
        country_code: product.country_code || null
      });
      setCart(response.data);
      setIsOpen(true);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 404) {
        try {
          const res = await axios.post(`${API}/cart`);
          const newId = res.data.cart_id;
          setCartId(newId);
          localStorage.setItem('cartId', newId);
          const retry = await axios.post(`${API}/cart/${newId}/items`, {
            product_id: product.product_id || product.id,
            name: product.name,
            price: parseFloat(product.price),
            quantity: 1,
            image: product.images?.[0]?.src || product.image || null,
            country: product.country_code || null,
            parent_id: product.parent_id || null,
            package_type_id: product.package_type_id || null,
            country_code: product.country_code || null
          });
          setCart(retry.data);
          setIsOpen(true);
          return true;
        } catch (retryError) {
          console.error('Error on cart retry:', retryError);
          return false;
        }
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  const removeFromCart = useCallback(async (productId) => {
    if (!cartId) return;
    const removedItem = cart.items.find(x => x.product_id === productId);
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/${cartId}/items/${productId}`);
      setCart(response.data);
      if (removedItem) trackRemoveFromCart(removedItem);
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  }, [cartId, cart.items]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (!cartId) return;
    setLoading(true);
    try {
      const response = await axios.put(`${API}/cart/${cartId}/items/${productId}?quantity=${quantity}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setLoading(false);
    }
  }, [cartId]);

  const clearCart = useCallback(() => {
    setCart({ items: [] });
    localStorage.removeItem('cartId');
    createCart();
  }, []);

  const cartTotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      cartId,
      cartCount,
      cartTotal,
      loading,
      isOpen,
      setIsOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
